import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useThemeContext } from './contexts/ThemeContext';
import { useAuth } from './contexts/AuthContext';
import { MainLayout, ContentArea } from './components/layout';

// Importación de iconos de Lucide React para la interfaz
import { 
  BarChart3,      // Icono para dashboard y gráficos
  TrendingUp,     // Icono para analíticas y tendencias
  Users,          // Icono para usuarios y gestión de personas
  UserPlus,       // Icono para agregar usuarios y reclutamiento
  Shield,         // Icono para seguridad y permisos
  DollarSign,     // Icono para finanzas y dinero
  Zap,            // Icono para rendimiento y eficiencia
  Monitor,        // Icono para monitoreo del sistema
  Paintbrush,     // Icono específico para pinturas
  FlaskConical,   // Icono para aditivos y formulación
  Wrench,         // Icono para herramientas y mantenimiento
  Settings,       // Icono para configuración
  FileText,       // Icono para documentos y reportes
  Database,       // Icono para inventario y datos
  Activity,       // Icono para actividad y procesos
  Target,         // Icono para objetivos y calidad
  Package,        // Icono para paquetes y almacenamiento
  Truck,          // Icono para logística y entregas
  Factory,        // Icono para instalaciones industriales
  CheckCircle,    // Icono para cumplimiento y verificación
  Upload,         // Icono para importación de datos
  Download,       // Icono para exportación de datos
  ShoppingCart,   // Icono para adquisiciones y compras
  ClipboardList,  // Icono para auditorías y checklists
  CalendarDays,   // Icono para mi agenda
  CalendarRange,  // Icono para planificación
  PackageCheck,   // Icono para recepción de mercadería
  Award,          // Icono para certificados
  AlertTriangle,  // Icono para no conformidades
  BadgeCheck,     // Icono para post-venta / conformidad
  BookOpen,       // Icono para catálogo de ítems
  Handshake,      // Icono para stakeholders
  Scale,          // Icono para patentes / propiedad intelectual
  Swords,         // Icono para competidores de producto
  Globe,          // Icono para mercado
  PieChart,       // Icono para análisis comercial
} from 'lucide-react';

import SectionPlaceholderPage from './components/placeholders/SectionPlaceholderPage';
import LeadsWorkspace from './components/paint/commercial/LeadsWorkspace';
import SummaryWorkspace from './components/paint/commercial/SummaryWorkspace';
import PaintProductsWorkspace from './components/paint/products/PaintProductsWorkspace';
import UpsellingWorkspace from './components/paint/products/UpsellingWorkspace';
import PaintCompetitorsWorkspace from './components/paint/products/PaintCompetitorsWorkspace';
import QuotationsWorkspace from './components/paint/commercial/QuotationsWorkspace';
import SalesWorkspace from './components/paint/commercial/SalesWorkspace';
import LicitationsWorkspace from './components/paint/commercial/LicitationsWorkspace';
import LogisticsWorkspace from './components/paint/commercial/LogisticsWorkspace';
import RequestsWorkspace from './components/paint/commercial/RequestsWorkspace';
import AfterSalesWorkspace from './components/paint/commercial/AfterSalesWorkspace';
import ProspectingWorkspace from './components/paint/commercial/ProspectingWorkspace';
import DashboardWorkspace from './components/backoffice/administration/DashboardWorkspace';
import FinanceSettingsWorkspace from './components/backoffice/finance/FinanceSettingsWorkspace';
import FinanceOverviewWorkspace from './components/backoffice/finance/FinanceOverviewWorkspace';
import FinanceTreasuryWorkspace from './components/backoffice/finance/FinanceTreasuryWorkspace';
import FinanceStakeholdersWorkspace from './components/backoffice/finance/FinanceStakeholdersWorkspace';
import FinanceAccountingWorkspace from './components/backoffice/finance/FinanceAccountingWorkspace';
import FinanceTaxesWorkspace from './components/backoffice/finance/FinanceTaxesWorkspace';
import FinancePurchasesWorkspace from './components/backoffice/finance/FinancePurchasesWorkspace';
import FinanceBudgetWorkspace from './components/backoffice/finance/FinanceBudgetWorkspace';
import FinancePayrollWorkspace from './components/backoffice/finance/FinancePayrollWorkspace';
import FinanceStatementsWorkspace from './components/backoffice/finance/FinanceStatementsWorkspace';
import FinanceFinancingWorkspace from './components/backoffice/finance/FinanceFinancingWorkspace';
import CollaboratorsWorkspace from './components/backoffice/hr/CollaboratorsWorkspace';
import ConfigurationWorkspace from './components/paint/commercial/ConfigurationWorkspace';
import AnalysisWorkspace from './components/paint/commercial/AnalysisWorkspace';
import ProcurementConfigurationWorkspace from './components/paint/acquisitions/ConfigurationWorkspace';
import SuppliersWorkspace from './components/paint/acquisitions/SuppliersWorkspace';
import PurchaseOrdersWorkspace from './components/paint/acquisitions/PurchaseOrdersWorkspace';
import PurchaseRequestsWorkspace from './components/paint/acquisitions/PurchaseRequestsWorkspace';
import PurchaseReceiptsWorkspace from './components/paint/acquisitions/PurchaseReceiptsWorkspace';
import ProcurementSummaryWorkspace from './components/paint/acquisitions/SummaryWorkspace';
import ProcurementQuotationsWorkspace from './components/paint/acquisitions/QuotationsWorkspace';
import ProcurementInventoryWorkspace from './components/paint/acquisitions/InventoryWorkspace';
import ProcurementCatalogWorkspace from './components/paint/acquisitions/CatalogWorkspace';
import ProductionRawMaterialsWorkspace from './components/paint/production/RawMaterialsWorkspace';
import ProductionFormulasWorkspace from './components/paint/production/FormulasWorkspace';
import ProductionPlanningWorkspace from './components/paint/production/PlanningWorkspace';
import ProductionShopWorkspace from './components/paint/production/ShopProductionWorkspace';
import ProductionConfigurationWorkspace from './components/paint/production/ConfigurationWorkspace';
import SystemUsersWorkspace from './components/system/administration/SystemUsersWorkspace';
import SystemActivityWorkspace from './components/system/administration/SystemActivityWorkspace';
import RolesAndPermissionsWorkspace from './components/system/administration/RolesAndPermissionsWorkspace';
import MaintenanceNoticeWorkspace from './components/system/administration/MaintenanceNoticeWorkspace';
import MaintenanceNoticeGate from './components/system/MaintenanceNoticeGate';
import SystemDataImportWorkspace from './components/system/datos/SystemDataImportWorkspace';
import SystemDataExportWorkspace from './components/system/datos/SystemDataExportWorkspace';
import AdministrationHubWorkspace from './components/system/AdministrationHubWorkspace';
import { isAdministrationPath } from './components/system/administrationCatalog';
import AdministrationUnlockGate from './components/system/AdministrationUnlockGate';
import UserProfileWorkspace from './components/placeholders/UserProfileWorkspace';
import UserAgendaWorkspace from './components/placeholders/UserAgendaWorkspace';
import PhenoxProductsWorkspace from './components/additives/products/PhenoxProductsWorkspace';
import AdditiveStandardsWorkspace from './components/additives/products/StandardsWorkspace';
import AdditivePatentsWorkspace from './components/additives/products/PatentsWorkspace';
import AdditiveIpFormulasWorkspace from './components/additives/products/IpFormulasWorkspace';
import AdditiveCompetitorsWorkspace from './components/additives/products/CompetitorsWorkspace';
import AdditiveCommercialConfigWorkspace from './components/additives/commercial/ConfigurationWorkspace';
import AdditiveMarketWorkspace from './components/additives/commercial/MarketWorkspace';
import {
  canAccessPath,
  filterBusinessUnitsByPermission,
  firstAllowedPath,
  isSystemAdmin,
  unitHasVisibleSection,
} from './utils/permissions';
import { ADDITIVES_UNIT, BusinessUnitProvider } from './contexts/BusinessUnitContext';
import { usePresence } from './hooks/usePresence';
import './App.css';

// Stubs to keep router signatures while pages are removed.
const EmptyPage = () => null;
const BackOfficeDashboard = EmptyPage;
const BackOfficeAnalytics = EmptyPage;
const BackOfficeMonitoring = EmptyPage;
const BackOfficeTreasury = EmptyPage;
const BackOfficeSettings = EmptyPage;
const AcquisitionsOverview = EmptyPage;
const PurchaseRequests = EmptyPage;
const Purchases = EmptyPage;
const PinturasFormulas = EmptyPage;
const PinturasBatches = EmptyPage;
const PinturasQuality = EmptyPage;
const PinturasInventory = EmptyPage;
const AditivosRecipes = EmptyPage;
const AditivosMixing = EmptyPage;
const AditivosTesting = EmptyPage;
const AditivosStorage = EmptyPage;
const ServiciosMaintenance = EmptyPage;
const ServiciosRepairs = EmptyPage;
const ServiciosInstallation = EmptyPage;
const ServiciosSupport = EmptyPage;
const SearchDesignDemo = EmptyPage;
const SearchComparisonDemo = EmptyPage;
const Table2Demo = EmptyPage;
const LeadsPage = EmptyPage;
const QuotationsPage = EmptyPage;
const LicitacionesPage = EmptyPage;
const PaintProductsPage = EmptyPage;
const UpsellingPlaceholder = EmptyPage;
const CommercialConfigurationPlaceholder = EmptyPage;
const CommercialProspectingPlaceholder = ProspectingWorkspace;
const CommercialTendersPlaceholder = EmptyPage;
const CommercialSalesPlaceholder = SalesWorkspace;
const CommercialRequestsPlaceholder = RequestsWorkspace;
const CommercialLogisticsPlaceholder = LogisticsWorkspace;
const CommercialAfterSalesPlaceholder = AfterSalesWorkspace;
const QualityPlaceholder = EmptyPage;
const InventoryPlaceholder = EmptyPage;
const OverviewPage = EmptyPage;
const TreasuryPage = EmptyPage;
const AccountingPage = EmptyPage;
const TaxesPage = EmptyPage;
const PurchasesPage = EmptyPage;
const BudgetPage = EmptyPage;
const PayrollPage = EmptyPage;
const FinancialStatementsPage = EmptyPage;
const FinancingPage = EmptyPage;
const FinanceSettingsPage = EmptyPage;
const UsersPage = EmptyPage;
const PermissionsPage = EmptyPage;
const MaintenancePage = EmptyPage;
const GeneralPage = EmptyPage;
const ConfigurationPage = EmptyPage;
const ActivityPage = EmptyPage;
const SessionsPage = EmptyPage;
const DataImportPage = EmptyPage;
const DataExportPage = EmptyPage;
const TeamPage = EmptyPage;
const CollaboratorsPage = CollaboratorsWorkspace;
const RecruitmentPage = EmptyPage;
const PerformancePage = EmptyPage;
const TrainingPage = EmptyPage;
const AttendancePage = EmptyPage;

// Wrapper component to pass isDarkMode to page components
const PATH_TITLE_MAP = {
  '/administracion': 'Administración',
  '/administracion/admin': 'Administración',
  '/administracion/company': 'Company',
  '/administracion/monitoring': 'Monitoreo',
  '/administracion/datos': 'Datos',
  '/sistema/admin/users': 'Usuarios',
  '/sistema/admin/permissions': 'Roles y permisos',
  '/sistema/admin/maintenance': 'Mantención',
  '/sistema/company/general': 'Configuración General',
  '/sistema/company/configuration': 'Ajustes de Empresa',
  '/sistema/monitoring/activity': 'Actividad del Sistema',
  '/sistema/monitoring/sessions': 'Sesiones',
  '/sistema/datos/importacion': 'Importación',
  '/sistema/datos/exportacion': 'Exportación',
  '/perfil': 'Mi Perfil',
  '/agenda': 'Mi Agenda',
  '/backoffice/dashboard': 'Dashboard BackOffice',
  '/backoffice/analytics': 'Analíticas',
  '/backoffice/monitoring': 'Monitoreo',
  '/backoffice/settings': 'Ajustes',
  '/backoffice/hr/collaborators': 'Colaboradores',
  '/backoffice/hr/team': 'Equipo',
  '/backoffice/hr/recruitment': 'Reclutamiento',
  '/backoffice/hr/performance': 'Desempeño',
  '/backoffice/hr/training': 'Capacitación',
  '/backoffice/hr/attendance': 'Asistencia',
  '/backoffice/finance/overview': 'Overview Finanzas',
  '/backoffice/finance/treasury': 'Tesorería',
  '/backoffice/finance/accounting': 'Contabilidad',
  '/backoffice/finance/stakeholders': 'Stakeholders',
  '/backoffice/finance/taxes': 'Impuestos',
  '/backoffice/finance/purchases': 'Compras',
  '/backoffice/finance/budget': 'Presupuesto',
  '/backoffice/finance/payroll': 'Nómina',
  '/backoffice/finance/statements': 'Estados Financieros',
  '/backoffice/finance/financing': 'Financiamiento',
  '/backoffice/finance/settings': 'Ajustes de Finanzas',
  '/backoffice/acquisitions/overview': 'Adquisiciones - Resumen',
  '/backoffice/acquisitions/purchase-requests': 'Solicitudes de Compra',
  '/backoffice/acquisitions/purchases': 'Compras (Adquisiciones)',
  '/pinturas/products/paint': 'Productos de Pintura',
  '/pinturas/products/upselling': 'Upselling',
  '/pinturas/products/competitors': 'Competidores',
  '/pinturas/commercial/summary': 'Resumen Comercial',
  '/pinturas/commercial/config': 'Ajustes Comercial',
  '/pinturas/commercial/analysis': 'Análisis Comercial',
  '/pinturas/commercial/leads': 'Leads',
  '/pinturas/commercial/prospecting': 'Prospección',
  '/pinturas/commercial/tenders': 'Licitaciones',
  '/pinturas/commercial/quotes': 'Cotizaciones',
  '/pinturas/commercial/sales': 'Ventas',
  '/pinturas/commercial/requests': 'Solicitudes',
  '/pinturas/commercial/logistics': 'Despachos',
  '/pinturas/commercial/after-sales': 'Post-Venta',
  '/pinturas/production/config': 'Ajustes Producción',
  '/pinturas/production/raw-materials': 'Materias primas',
  '/pinturas/production/formulas': 'Fórmulas',
  '/pinturas/production/planning': 'Planificación',
  '/pinturas/production/shop': 'Producción',
  '/pinturas/production/batches': 'Lotes',
  '/pinturas/quality': 'Control de Calidad',
  '/pinturas/quality/nonconformities': 'No Conformidades',
  '/pinturas/quality/certificates': 'Certificados',
  '/pinturas/quality/audits': 'Auditorías',
  '/pinturas/testing': 'Pruebas',
  '/pinturas/inventory': 'Inventario',
  '/pinturas/acquisitions/summary': 'Resumen Adquisiciones',
  '/pinturas/acquisitions/config': 'Ajustes Adquisiciones',
  '/pinturas/acquisitions/purchase-requests': 'Solicitudes',
  '/pinturas/acquisitions/purchase-orders': 'Órdenes de Compra',
  '/pinturas/acquisitions/receipts': 'Recepción',
  '/pinturas/acquisitions/quotations': 'Cotizaciones',
  '/pinturas/acquisitions/inventory': 'Inventarios',
  '/pinturas/acquisitions/catalog': 'Catálogo',
  '/pinturas/suppliers': 'Proveedores',
  '/aditivos/products/standards': 'Normas y Certificaciones',
  '/aditivos/products/phenox': 'Productos PhenoX',
  '/aditivos/products/competitors': 'Competidores',
  '/aditivos/products/patents': 'Patentes',
  '/aditivos/products/formulas': 'Fórmulas',
  '/aditivos/commercial/summary': 'Resumen Comercial',
  '/aditivos/commercial/config': 'Ajustes Comercial',
  '/aditivos/commercial/leads': 'Leads',
  '/aditivos/commercial/prospecting': 'Prospección',
  '/aditivos/commercial/market': 'Mercado',
  '/aditivos/recipes': 'Recetas',
  '/aditivos/mixing': 'Mezclado',
  '/aditivos/testing': 'Pruebas de Aditivos',
  '/aditivos/storage': 'Almacenamiento',
  '/servicios/maintenance': 'Mantenimiento',
  '/servicios/repairs': 'Reparaciones',
  '/servicios/installation': 'Instalaciones',
  '/servicios/support': 'Soporte',
};

const HomeRedirect = () => {
  const { user } = useAuth();
  return <Navigate to={firstAllowedPath(user)} replace />;
};

const ADDITIVES_COMMERCIAL_PAGES = {
  '/aditivos/commercial/summary': SummaryWorkspace,
  '/aditivos/commercial/config': AdditiveCommercialConfigWorkspace,
  '/aditivos/commercial/leads': LeadsWorkspace,
  '/aditivos/commercial/prospecting': ProspectingWorkspace,
  '/aditivos/commercial/market': AdditiveMarketWorkspace,
};

const ADDITIVES_PRODUCT_PAGES = {
  '/aditivos/products/standards': AdditiveStandardsWorkspace,
  '/aditivos/products/phenox': PhenoxProductsWorkspace,
  '/aditivos/products/competitors': AdditiveCompetitorsWorkspace,
  '/aditivos/products/patents': AdditivePatentsWorkspace,
  '/aditivos/products/formulas': AdditiveIpFormulasWorkspace,
};

const AditivosHomeRedirect = () => {
  const { user } = useAuth();
  const target = canAccessPath(user, '/aditivos/commercial/summary')
    ? '/aditivos/commercial/summary'
    : '/aditivos/recipes';
  return <Navigate to={target} replace />;
};

const PageWrapper = () => {
  const { isDarkMode } = useThemeContext();
  const { user } = useAuth();
  const location = useLocation();

  if (user && !canAccessPath(user, location.pathname)) {
    return <Navigate to={firstAllowedPath(user)} replace />;
  }

  const withAdminBack = (node) =>
    React.isValidElement(node)
      ? React.cloneElement(node, { showAdminBack: true })
      : node;

  let page = null;

  if (location.pathname === '/administracion' || location.pathname.startsWith('/administracion/')) {
    page = <AdministrationHubWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/leads') {
    page = <LeadsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/summary') {
    page = <SummaryWorkspace isDarkMode={isDarkMode} />;
  } else if (ADDITIVES_PRODUCT_PAGES[location.pathname]) {
    const AdditivesProductPage = ADDITIVES_PRODUCT_PAGES[location.pathname];
    page = <AdditivesProductPage isDarkMode={isDarkMode} />;
  } else if (ADDITIVES_COMMERCIAL_PAGES[location.pathname]) {
    const AdditivesPage = ADDITIVES_COMMERCIAL_PAGES[location.pathname];
    page = (
      <BusinessUnitProvider unit={ADDITIVES_UNIT}>
        <AdditivesPage isDarkMode={isDarkMode} />
      </BusinessUnitProvider>
    );
  } else if (location.pathname === '/sistema/admin/users') {
    page = withAdminBack(<SystemUsersWorkspace isDarkMode={isDarkMode} />);
  } else if (location.pathname === '/sistema/monitoring/activity') {
    page = withAdminBack(<SystemActivityWorkspace isDarkMode={isDarkMode} />);
  } else if (location.pathname === '/sistema/admin/permissions') {
    page = withAdminBack(<RolesAndPermissionsWorkspace isDarkMode={isDarkMode} />);
  } else if (location.pathname === '/sistema/admin/maintenance') {
    page = withAdminBack(<MaintenanceNoticeWorkspace isDarkMode={isDarkMode} />);
  } else if (location.pathname === '/sistema/datos/importacion') {
    page = withAdminBack(<SystemDataImportWorkspace isDarkMode={isDarkMode} />);
  } else if (location.pathname === '/sistema/datos/exportacion') {
    page = withAdminBack(<SystemDataExportWorkspace isDarkMode={isDarkMode} />);
  } else if (location.pathname === '/perfil') {
    page = <UserProfileWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/agenda') {
    page = <UserAgendaWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/dashboard') {
    page = <DashboardWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/settings') {
    page = <FinanceSettingsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/overview') {
    page = <FinanceOverviewWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/treasury') {
    page = <FinanceTreasuryWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/accounting') {
    page = <FinanceAccountingWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/stakeholders') {
    page = <FinanceStakeholdersWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/taxes') {
    page = <FinanceTaxesWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/purchases') {
    page = <FinancePurchasesWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/budget') {
    page = <FinanceBudgetWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/payroll') {
    page = <FinancePayrollWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/statements') {
    page = <FinanceStatementsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/backoffice/finance/financing') {
    page = <FinanceFinancingWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/quotes') {
    page = <QuotationsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/sales') {
    page = <SalesWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/requests') {
    page = <RequestsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/tenders') {
    page = <LicitationsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/logistics') {
    page = <LogisticsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/after-sales') {
    page = <AfterSalesWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/prospecting') {
    page = <ProspectingWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/config') {
    page = <ConfigurationWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/commercial/analysis') {
    page = <AnalysisWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/summary') {
    page = <ProcurementSummaryWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/config') {
    page = <ProcurementConfigurationWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/suppliers') {
    page = <SuppliersWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/purchase-requests') {
    page = <PurchaseRequestsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/purchase-orders') {
    page = <PurchaseOrdersWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/receipts') {
    page = <PurchaseReceiptsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/quotations') {
    page = <ProcurementQuotationsWorkspace isDarkMode={isDarkMode} />;
  } else if (
    location.pathname === '/pinturas/acquisitions/inventory' ||
    location.pathname === '/pinturas/acquisitions/raw-materials'
  ) {
    page = <ProcurementInventoryWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/acquisitions/catalog') {
    page = <ProcurementCatalogWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/products/paint') {
    page = <PaintProductsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/products/upselling') {
    page = <UpsellingWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/products/competitors') {
    page = <PaintCompetitorsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/production/config') {
    page = <ProductionConfigurationWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/production/raw-materials') {
    page = <ProductionRawMaterialsWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/production/formulas') {
    page = <ProductionFormulasWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/production/planning') {
    page = <ProductionPlanningWorkspace isDarkMode={isDarkMode} />;
  } else if (location.pathname === '/pinturas/production/shop') {
    page = <ProductionShopWorkspace isDarkMode={isDarkMode} />;
  } else {
    const title = PATH_TITLE_MAP[location.pathname] || 'Sección';
    const placeholder = (
      <SectionPlaceholderPage
        isDarkMode={isDarkMode}
        title={title}
        description={`Aquí se mostrará el contenido principal de ${title.toLowerCase()}.`}
        detail="Métricas, tablas y visualizaciones en desarrollo"
      />
    );
    page = location.pathname.startsWith('/sistema/') ? withAdminBack(placeholder) : placeholder;
  }

  if (isAdministrationPath(location.pathname)) {
    return (
      <AdministrationUnlockGate isDarkMode={isDarkMode}>
        {page}
      </AdministrationUnlockGate>
    );
  }

  return page;
};

// Componente interno que maneja el layout y routing
const LAST_BUSINESS_UNIT_KEY = 'sqmt_last_business_unit';

const resolveTopBusinessUnit = (path = '') => {
  if (path.startsWith('/pinturas')) return 'Pinturas';
  if (path.startsWith('/aditivos')) return 'Aditivos';
  if (path.startsWith('/backoffice')) return 'BackOffice';
  return null;
};

const AppLayout = () => {
  const { isDarkMode } = useThemeContext();
  const { user, logout } = useAuth();
  const { canView: canViewPresence, activeUsers, activeCount } = usePresence();
  const [presenceOpen, setPresenceOpen] = useState(false);
  const [viewRefreshKey, setViewRefreshKey] = useState(0);
  const location = useLocation();
  const pathUnit = resolveTopBusinessUnit(location.pathname);
  const [sidebarUnit, setSidebarUnit] = useState(() => {
    try {
      return localStorage.getItem(LAST_BUSINESS_UNIT_KEY) || 'Pinturas';
    } catch {
      return 'Pinturas';
    }
  });

  useEffect(() => {
    if (!pathUnit) return;
    setSidebarUnit(pathUnit);
    try {
      localStorage.setItem(LAST_BUSINESS_UNIT_KEY, pathUnit);
    } catch {
      // ignore storage errors
    }
  }, [pathUnit]);

  const activeNavItem = pathUnit;
  const sidebarBusinessUnit = pathUnit || sidebarUnit;

  /**
   * Configuración de Business Units
   * Define la estructura completa de cada unidad de negocio con sus secciones y items
   */
  const businessUnits = {
    // Business Unit BackOffice - Administración y gestión general
    BackOffice: {
      sections: [
        {
          key: "administration",
          title: "Administración",
          collapsible: true,
          items: [
            { key: "dashboard", text: "Dashboard", href: "/backoffice/dashboard", icon: <BarChart3 size={16} /> },
            { key: "analytics", text: "Analíticas", href: "/backoffice/analytics", icon: <TrendingUp size={16} /> },
            { key: "monitoring", text: "Monitoreo", href: "/backoffice/monitoring", icon: <Monitor size={16} /> },
            { key: "settings", text: "Ajustes", href: "/backoffice/settings", icon: <Settings size={16} /> }
          ]
        },
        {
          key: "human-resources",
          title: "Recursos Humanos",
          collapsible: true,
          items: [
            { key: "collaborators", text: "Colaboradores", href: "/backoffice/hr/collaborators", icon: <Users size={16} /> },
            { key: "recruitment", text: "Reclutamiento", href: "/backoffice/hr/recruitment", icon: <UserPlus size={16} /> },
            { key: "performance", text: "Desempeño", href: "/backoffice/hr/performance", icon: <TrendingUp size={16} /> },
            { key: "training", text: "Capacitación", href: "/backoffice/hr/training", icon: <BarChart3 size={16} /> },
            { key: "attendance", text: "Asistencia", href: "/backoffice/hr/attendance", icon: <CheckCircle size={16} /> }
          ]
        },
        {
          key: "finance",
          title: "Finanzas",
          collapsible: true,
          items: [
            { key: "finance-overview", text: "Overview", href: "/backoffice/finance/overview", icon: <BarChart3 size={16} /> },
            { key: "treasury", text: "Tesorería", href: "/backoffice/finance/treasury", icon: <DollarSign size={16} /> },
            { key: "accounting", text: "Contabilidad", href: "/backoffice/finance/accounting", icon: <FileText size={16} /> },
            { key: "stakeholders", text: "Stakeholders", href: "/backoffice/finance/stakeholders", icon: <Handshake size={16} /> },
            { key: "taxes", text: "Impuestos", href: "/backoffice/finance/taxes", icon: <FileText size={16} /> },
            { key: "purchases", text: "Compras", href: "/backoffice/finance/purchases", icon: <Package size={16} /> },
            { key: "budget", text: "Presupuesto", href: "/backoffice/finance/budget", icon: <BarChart3 size={16} /> },
            { key: "payroll", text: "Nómina", href: "/backoffice/finance/payroll", icon: <Users size={16} /> },
            { key: "statements", text: "Estados Financieros", href: "/backoffice/finance/statements", icon: <FileText size={16} /> },
            { key: "financing", text: "Financiamiento", href: "/backoffice/finance/financing", icon: <DollarSign size={16} /> },
            { key: "finance-settings", text: "Ajustes", href: "/backoffice/finance/settings", icon: <Settings size={16} /> }
          ]
        }
      ]
    },

    // Business Unit Pinturas - Gestión completa de pinturas
    Pinturas: {
      sections: [
        {
          key: "products",
          title: "Productos",
          collapsible: true,
          items: [
            { key: "paint-products", text: "Pinturas", href: "/pinturas/products/paint", icon: <Paintbrush size={16} /> },
            { key: "upselling", text: "Upselling", href: "/pinturas/products/upselling", icon: <TrendingUp size={16} /> },
            { key: "paint-competitors", text: "Competidores", href: "/pinturas/products/competitors", icon: <Swords size={16} /> }
          ]
        },
        {
          key: "commercial",
          title: "Comercial",
          collapsible: true,
          items: [
            { key: "commercial-summary", text: "Resumen", href: "/pinturas/commercial/summary", icon: <BarChart3 size={16} /> },
            { key: "commercial-config", text: "Ajustes", href: "/pinturas/commercial/config", icon: <Settings size={16} /> },
            { key: "commercial-analysis", text: "Análisis", href: "/pinturas/commercial/analysis", icon: <PieChart size={16} /> },
            { key: "commercial-leads", text: "Leads", href: "/pinturas/commercial/leads", icon: <Users size={16} /> },
            { key: "commercial-prospecting", text: "Prospección", href: "/pinturas/commercial/prospecting", icon: <Target size={16} /> },
            { key: "commercial-tenders", text: "Licitaciones", href: "/pinturas/commercial/tenders", icon: <FileText size={16} /> },
            { key: "commercial-quotes", text: "Cotizaciones", href: "/pinturas/commercial/quotes", icon: <DollarSign size={16} /> },
            { key: "commercial-sales", text: "Ventas", href: "/pinturas/commercial/sales", icon: <Activity size={16} /> },
            { key: "commercial-requests", text: "Solicitudes", href: "/pinturas/commercial/requests", icon: <ClipboardList size={16} /> },
            { key: "commercial-logistics", text: "Despachos", href: "/pinturas/commercial/logistics", icon: <Truck size={16} /> },
            { key: "commercial-after-sales", text: "Post-Venta", href: "/pinturas/commercial/after-sales", icon: <BadgeCheck size={16} /> }
          ]
        },
        {
          key: "production",
          title: "Producción",
          collapsible: true,
          items: [
            { key: "production-config", text: "Ajustes", href: "/pinturas/production/config", icon: <Settings size={16} /> },
            { key: "raw-materials", text: "Materias Primas", href: "/pinturas/production/raw-materials", icon: <FlaskConical size={16} /> },
            { key: "formulas", text: "Fórmulas", href: "/pinturas/production/formulas", icon: <Paintbrush size={16} /> },
            { key: "planning", text: "Planificación", href: "/pinturas/production/planning", icon: <CalendarRange size={16} /> },
            { key: "shop", text: "Producción", href: "/pinturas/production/shop", icon: <Factory size={16} /> },
            { key: "quality-control", text: "Control de Calidad", href: "/pinturas/quality", icon: <Target size={16} /> },
            { key: "inventory", text: "Inventario", href: "/pinturas/inventory", icon: <Database size={16} /> }
          ]
        },
        {
          key: "acquisitions",
          title: "Adquisiciones",
          collapsible: true,
          items: [
            { key: "acquisitions-summary", text: "Resumen", href: "/pinturas/acquisitions/summary", icon: <BarChart3 size={16} /> },
            { key: "acquisitions-config", text: "Ajustes", href: "/pinturas/acquisitions/config", icon: <Settings size={16} /> },
            { key: "suppliers", text: "Proveedores", href: "/pinturas/suppliers", icon: <Truck size={16} /> },
            { key: "purchase-requests", text: "Solicitudes", href: "/pinturas/acquisitions/purchase-requests", icon: <ClipboardList size={16} /> },
            { key: "purchase-orders", text: "Órdenes de Compra", href: "/pinturas/acquisitions/purchase-orders", icon: <ShoppingCart size={16} /> },
            { key: "purchase-receipts", text: "Recepción", href: "/pinturas/acquisitions/receipts", icon: <PackageCheck size={16} /> },
            { key: "purchase-quotations", text: "Cotizaciones", href: "/pinturas/acquisitions/quotations", icon: <FileText size={16} /> },
            { key: "procurement-catalog", text: "Catálogo", href: "/pinturas/acquisitions/catalog", icon: <BookOpen size={16} /> },
            { key: "procurement-inventory", text: "Inventarios", href: "/pinturas/acquisitions/inventory", icon: <Package size={16} /> }
          ]
        },
        {
          key: "quality",
          title: "Gestión de Calidad",
          collapsible: true,
          items: [
            { key: "testing", text: "Pruebas", href: "/pinturas/testing", icon: <CheckCircle size={16} /> },
            { key: "nonconformities", text: "No Conformidades", href: "/pinturas/quality/nonconformities", icon: <AlertTriangle size={16} /> },
            { key: "certificates", text: "Certificados", href: "/pinturas/quality/certificates", icon: <Award size={16} /> },
            { key: "audits", text: "Auditorías", href: "/pinturas/quality/audits", icon: <ClipboardList size={16} /> }
          ]
        }
      ]
    },

    // Business Unit Aditivos - Gestión de aditivos y formulación
    Aditivos: {
      sections: [
        {
          key: "additives-products",
          title: "Productos",
          collapsible: true,
          items: [
            { key: "additives-standards", text: "Normas y Certificaciones", href: "/aditivos/products/standards", icon: <Award size={16} /> },
            { key: "additives-phenox", text: "Productos PhenoX", href: "/aditivos/products/phenox", icon: <FlaskConical size={16} /> },
            { key: "additives-competitors", text: "Competidores", href: "/aditivos/products/competitors", icon: <Swords size={16} /> }
          ]
        },
        {
          key: "additives-ip",
          title: "Propiedad Intelectual",
          collapsible: true,
          items: [
            { key: "additives-patents", text: "Patentes", href: "/aditivos/products/patents", icon: <Scale size={16} /> },
            { key: "additives-formulas", text: "Fórmulas", href: "/aditivos/products/formulas", icon: <BookOpen size={16} /> }
          ]
        },
        {
          key: "additives-commercial",
          title: "Comercial",
          collapsible: true,
          items: [
            { key: "additives-commercial-summary", text: "Resumen", href: "/aditivos/commercial/summary", icon: <BarChart3 size={16} /> },
            { key: "additives-commercial-config", text: "Ajustes", href: "/aditivos/commercial/config", icon: <Settings size={16} /> },
            { key: "additives-commercial-leads", text: "Leads", href: "/aditivos/commercial/leads", icon: <Users size={16} /> },
            { key: "additives-commercial-prospecting", text: "Prospección", href: "/aditivos/commercial/prospecting", icon: <Target size={16} /> },
            { key: "additives-commercial-market", text: "Mercado", href: "/aditivos/commercial/market", icon: <Globe size={16} /> }
          ]
        },
        {
          key: "formulation",
          title: "Formulación",
          collapsible: false,
          items: [
            { key: "recipes", text: "Recetas", href: "/aditivos/recipes", icon: <FlaskConical size={16} /> },
            { key: "mixing", text: "Mezclado", href: "/aditivos/mixing", icon: <Zap size={16} /> }
          ]
        },
        {
          key: "testing",
          title: "Pruebas",
          collapsible: true,
          items: [
            { key: "testing", text: "Pruebas", href: "/aditivos/testing", icon: <Activity size={16} /> },
            { key: "analysis", text: "Análisis", href: "/aditivos/analysis", icon: <Target size={16} /> }
          ]
        },
        {
          key: "storage",
          title: "Almacenamiento",
          collapsible: true,
          items: [
            { key: "storage", text: "Almacén", href: "/aditivos/storage", icon: <Package size={16} /> },
            { key: "logistics", text: "Despachos", href: "/aditivos/logistics", icon: <Truck size={16} /> }
          ]
        }
      ]
    },

    // Business Unit Servicios - Servicios técnicos y mantenimiento
    Servicios: {
      sections: [
        {
          key: "maintenance",
          title: "Mantenimiento",
          collapsible: false,
          items: [
            { key: "maintenance", text: "Mantenimiento", href: "/servicios/maintenance", icon: <Wrench size={16} /> },
            { key: "repairs", text: "Reparaciones", href: "/servicios/repairs", icon: <Factory size={16} /> }
          ]
        },
        {
          key: "installation",
          title: "Instalación",
          collapsible: true,
          items: [
            { key: "installation", text: "Instalaciones", href: "/servicios/installation", icon: <Factory size={16} /> },
            { key: "support", text: "Soporte", href: "/servicios/support", icon: <Shield size={16} /> }
          ]
        },
        {
          key: "billing",
          title: "Facturación",
          collapsible: true,
          items: [
            { key: "quotes", text: "Cotizaciones", href: "/servicios/billing/quotes", icon: <FileText size={16} /> },
            { key: "invoices", text: "Facturas", href: "/servicios/billing/invoices", icon: <DollarSign size={16} /> },
            { key: "payments", text: "Pagos", href: "/servicios/billing/payments", icon: <TrendingUp size={16} /> }
          ]
        }
      ]
    }
  };

  /**
   * Configuración de navegación principal (business units)
   * Orden: Pinturas → Aditivos → BackOffice. Servicios oculto. Sistema movido a sidebar.
   */
  const navigationItems = [
    { 
      text: "Pinturas", 
      href: "/pinturas", 
      active: activeNavItem === "Pinturas",
      onClick: () => {}
    },
    { 
      text: "Aditivos", 
      href: "/aditivos", 
      active: activeNavItem === "Aditivos",
      onClick: () => {}
    },
    { 
      text: "BackOffice",
      href: "/backoffice",
      active: activeNavItem === "BackOffice",
      onClick: () => {}
    },
  ].filter((item) => unitHasVisibleSection(user, item.text));

  /**
   * Procesamiento dinámico del sidebar izquierdo
   */
  const filteredBusinessUnits = filterBusinessUnitsByPermission(businessUnits, user);
  const currentBusinessUnit =
    filteredBusinessUnits[sidebarBusinessUnit] ||
    filteredBusinessUnits["Pinturas"] ||
    filteredBusinessUnits["BackOffice"] ||
    { sections: [] };
  const leftSections = currentBusinessUnit.sections;
  const adminNav = isSystemAdmin(user)
    ? {
        key: 'admin',
        text: 'Administración',
        href: '/administracion',
        active: isAdministrationPath(location.pathname),
        icon: <Settings size={16} />,
      }
    : null;
  const footerNavItems = [
    {
      key: 'agenda',
      text: 'Mi agenda',
      href: '/agenda',
      active: location.pathname === '/agenda',
      icon: <CalendarDays size={16} />,
    },
    ...(adminNav ? [adminNav] : []),
  ];

  /**
   * Footer: usuarios activos solo para Admin/Gerente (permiso monitoring.presence).
   */
  const presenceNames = activeUsers
    .map((u) => u.full_name || u.email)
    .filter(Boolean)
    .join(' · ');
  const footerStatusItems = canViewPresence
    ? [
        {
          key: 'users-online',
          label: 'Usuarios',
          value: activeCount === 1 ? '1 activo' : `${activeCount} activos`,
          status: activeCount > 0 ? 'Online' : 'Idle',
          color: activeCount > 0 ? '#28a745' : '#6c757d',
          tooltip:
            activeCount > 0
              ? presenceNames || 'Usuarios activos'
              : 'No hay otros usuarios activos ahora',
          onClick: () => setPresenceOpen((open) => !open),
        },
      ]
    : [];

  return (
    <>
    <MainLayout
      logo="Soquimat ERP"
      navigationItems={navigationItems}
      activeNavItem={activeNavItem || sidebarBusinessUnit}
      searchPlaceholder={`Buscar en ${sidebarBusinessUnit}...`}
      searchShortcut="⌘K"
      user={user}
      onLogout={logout}
      onRefresh={() => setViewRefreshKey((key) => key + 1)}
      leftSections={leftSections}
      adminNav={adminNav}
      footerNavItems={footerNavItems}
      footerStatusItems={footerStatusItems}
      isDarkMode={isDarkMode}
    >
      <ContentArea isDarkMode={isDarkMode}>
        <Routes key={viewRefreshKey}>
        {/* Demo Routes */}
        <Route path="/demo/search-design" element={<PageWrapper Component={SearchDesignDemo} />} />
        <Route path="/demo/search-comparison" element={<PageWrapper Component={SearchComparisonDemo} />} />
        <Route path="/demo/table2" element={<PageWrapper Component={Table2Demo} />} />
        
          {/* Administración (antes Sistema en barra superior) */}
          <Route path="/administracion" element={<PageWrapper />} />
          <Route path="/administracion/:categoryKey" element={<PageWrapper />} />
          <Route path="/sistema" element={<Navigate to="/administracion" replace />} />
          
          {/* Sistema - Administración */}
          <Route path="/sistema/admin/users" element={<PageWrapper Component={UsersPage} />} />
          <Route path="/sistema/admin/permissions" element={<PageWrapper Component={PermissionsPage} />} />
          <Route path="/sistema/admin/maintenance" element={<PageWrapper Component={MaintenancePage} />} />
          
          {/* Sistema - Company */}
          <Route path="/sistema/company/general" element={<PageWrapper Component={GeneralPage} />} />
          <Route path="/sistema/company/configuration" element={<PageWrapper Component={ConfigurationPage} />} />
          
          {/* Sistema - Monitoreo */}
          <Route path="/sistema/monitoring/activity" element={<PageWrapper Component={ActivityPage} />} />
          <Route path="/sistema/monitoring/sessions" element={<PageWrapper Component={SessionsPage} />} />
          {/* Sistema - Datos */}
          <Route path="/sistema/datos/importacion" element={<PageWrapper Component={DataImportPage} />} />
          <Route path="/sistema/datos/exportacion" element={<PageWrapper Component={DataExportPage} />} />
          
          {/* BackOffice Routes */}
          <Route path="/backoffice" element={<Navigate to="/backoffice/dashboard" replace />} />
          <Route path="/backoffice/dashboard" element={<PageWrapper Component={BackOfficeDashboard} />} />
          <Route path="/backoffice/analytics" element={<PageWrapper Component={BackOfficeAnalytics} />} />
          <Route path="/backoffice/monitoring" element={<PageWrapper Component={BackOfficeMonitoring} />} />
          <Route path="/backoffice/settings" element={<PageWrapper Component={BackOfficeSettings} />} />
          
          {/* HR Routes */}
          <Route path="/backoffice/hr" element={<Navigate to="/backoffice/hr/collaborators" replace />} />
          <Route path="/backoffice/hr/collaborators" element={<PageWrapper Component={CollaboratorsPage} />} />
          <Route path="/backoffice/hr/team" element={<Navigate to="/backoffice/hr/collaborators" replace />} />
          <Route path="/backoffice/hr/recruitment" element={<PageWrapper Component={RecruitmentPage} />} />
          <Route path="/backoffice/hr/performance" element={<PageWrapper Component={PerformancePage} />} />
          <Route path="/backoffice/hr/training" element={<PageWrapper Component={TrainingPage} />} />
          <Route path="/backoffice/hr/attendance" element={<PageWrapper Component={AttendancePage} />} />
          
          {/* Finance Routes */}
          <Route path="/backoffice/finance" element={<Navigate to="/backoffice/finance/overview" replace />} />
          <Route path="/backoffice/finance/overview" element={<PageWrapper Component={OverviewPage} />} />
          <Route path="/backoffice/finance/treasury" element={<PageWrapper Component={TreasuryPage} />} />
          <Route path="/backoffice/finance/stakeholders" element={<PageWrapper Component={TreasuryPage} />} />
          <Route path="/backoffice/finance/accounting" element={<PageWrapper Component={AccountingPage} />} />
          <Route path="/backoffice/finance/taxes" element={<PageWrapper Component={TaxesPage} />} />
          <Route path="/backoffice/finance/purchases" element={<PageWrapper Component={PurchasesPage} />} />
          <Route path="/backoffice/finance/budget" element={<PageWrapper Component={BudgetPage} />} />
          <Route path="/backoffice/finance/payroll" element={<PageWrapper Component={PayrollPage} />} />
          <Route path="/backoffice/finance/statements" element={<PageWrapper Component={FinancialStatementsPage} />} />
          <Route path="/backoffice/finance/financing" element={<PageWrapper Component={FinancingPage} />} />
          <Route path="/backoffice/finance/settings" element={<PageWrapper Component={FinanceSettingsPage} />} />
          
          <Route path="/backoffice/settings" element={<PageWrapper Component={BackOfficeSettings} />} />
          
          {/* Acquisitions Routes */}
          <Route path="/backoffice/acquisitions" element={<Navigate to="/backoffice/acquisitions/overview" replace />} />
          <Route path="/backoffice/acquisitions/overview" element={<PageWrapper Component={AcquisitionsOverview} />} />
          <Route path="/backoffice/acquisitions/purchase-requests" element={<PageWrapper Component={PurchaseRequests} />} />
          <Route path="/backoffice/acquisitions/purchases" element={<PageWrapper Component={Purchases} />} />
          
          {/* Pinturas Routes */}
          <Route path="/pinturas" element={<Navigate to="/pinturas/products/paint" replace />} />
          
          {/* Products Division */}
          <Route path="/pinturas/products/paint" element={<PageWrapper Component={PaintProductsPage} />} />
          <Route path="/pinturas/products/upselling" element={<PageWrapper Component={UpsellingPlaceholder} />} />
          <Route path="/pinturas/products/competitors" element={<PageWrapper />} />
          
          {/* Commercial Division */}
          <Route path="/pinturas/commercial/summary" element={<PageWrapper Component={SummaryWorkspace} />} />
          <Route path="/pinturas/commercial/config" element={<PageWrapper Component={CommercialConfigurationPlaceholder} />} />
          <Route path="/pinturas/commercial/analysis" element={<PageWrapper Component={AnalysisWorkspace} />} />
          <Route path="/pinturas/commercial/leads" element={<PageWrapper Component={LeadsPage} />} />
          <Route path="/pinturas/commercial/prospecting" element={<PageWrapper Component={CommercialProspectingPlaceholder} />} />
          <Route path="/pinturas/commercial/tenders" element={<PageWrapper Component={LicitacionesPage} />} />
          <Route path="/pinturas/commercial/quotes" element={<PageWrapper Component={QuotationsPage} />} />
          <Route path="/pinturas/commercial/sales" element={<PageWrapper Component={CommercialSalesPlaceholder} />} />
          <Route path="/pinturas/commercial/requests" element={<PageWrapper Component={CommercialRequestsPlaceholder} />} />
          <Route path="/pinturas/commercial/logistics" element={<PageWrapper Component={CommercialLogisticsPlaceholder} />} />
          <Route path="/pinturas/commercial/after-sales" element={<PageWrapper Component={CommercialAfterSalesPlaceholder} />} />
          
          {/* Production Division */}
          <Route path="/pinturas/production/config" element={<PageWrapper Component={ProductionConfigurationWorkspace} />} />
          <Route path="/pinturas/production/raw-materials" element={<PageWrapper Component={ProductionRawMaterialsWorkspace} />} />
          <Route path="/pinturas/production/formulas" element={<PageWrapper Component={ProductionFormulasWorkspace} />} />
          <Route path="/pinturas/production/planning" element={<PageWrapper Component={ProductionPlanningWorkspace} />} />
          <Route path="/pinturas/production/shop" element={<PageWrapper Component={ProductionShopWorkspace} />} />
          <Route path="/pinturas/production/batches" element={<Navigate to="/pinturas/production/planning" replace />} />
          
          {/* Quality Division */}
          <Route path="/pinturas/quality" element={<PageWrapper Component={PinturasQuality} />} />
          <Route path="/pinturas/quality/nonconformities" element={<PageWrapper Component={QualityPlaceholder} />} />
          <Route path="/pinturas/quality/certificates" element={<PageWrapper Component={QualityPlaceholder} />} />
          <Route path="/pinturas/quality/audits" element={<PageWrapper Component={QualityPlaceholder} />} />
          <Route path="/pinturas/testing" element={<PageWrapper Component={QualityPlaceholder} />} />
          
          {/* Inventory Division */}
          <Route path="/pinturas/inventory" element={<PageWrapper Component={PinturasInventory} />} />
          <Route path="/pinturas/suppliers" element={<PageWrapper Component={InventoryPlaceholder} />} />
          
          {/* Acquisitions Division (Pinturas) */}
          <Route path="/pinturas/acquisitions/summary" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/config" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/purchase-requests" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/purchase-orders" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/receipts" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/quotations" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/inventory" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/catalog" element={<PageWrapper Component={InventoryPlaceholder} />} />
          <Route path="/pinturas/acquisitions/raw-materials" element={<Navigate to="/pinturas/acquisitions/inventory" replace />} />
          
          {/* Aditivos Routes */}
          <Route path="/aditivos" element={<AditivosHomeRedirect />} />
          <Route path="/aditivos/products" element={<Navigate to="/aditivos/products/phenox" replace />} />
          <Route path="/aditivos/products/standards" element={<PageWrapper />} />
          <Route path="/aditivos/products/phenox" element={<PageWrapper />} />
          <Route path="/aditivos/products/competitors" element={<PageWrapper />} />
          <Route path="/aditivos/products/patents" element={<PageWrapper />} />
          <Route path="/aditivos/products/formulas" element={<PageWrapper />} />
          <Route path="/aditivos/commercial/summary" element={<PageWrapper />} />
          <Route path="/aditivos/commercial/config" element={<PageWrapper />} />
          <Route path="/aditivos/commercial/leads" element={<PageWrapper />} />
          <Route path="/aditivos/commercial/prospecting" element={<PageWrapper />} />
          <Route path="/aditivos/commercial/market" element={<PageWrapper />} />
          <Route path="/aditivos/recipes" element={<PageWrapper Component={AditivosRecipes} />} />
          <Route path="/aditivos/recipes/" element={<PageWrapper Component={AditivosRecipes} />} />
          <Route path="/aditivos/mixing" element={<PageWrapper Component={AditivosMixing} />} />
          <Route path="/aditivos/mixing/" element={<PageWrapper Component={AditivosMixing} />} />
          <Route path="/aditivos/analysis" element={<PageWrapper Component={AditivosTesting} />} />
          <Route path="/aditivos/analysis/" element={<PageWrapper Component={AditivosTesting} />} />
          <Route path="/aditivos/testing" element={<PageWrapper Component={AditivosTesting} />} />
          <Route path="/aditivos/testing/" element={<PageWrapper Component={AditivosTesting} />} />
          <Route path="/aditivos/storage" element={<PageWrapper Component={AditivosStorage} />} />
          <Route path="/aditivos/storage/" element={<PageWrapper Component={AditivosStorage} />} />
          <Route path="/aditivos/logistics" element={<PageWrapper Component={AditivosStorage} />} />
          <Route path="/aditivos/logistics/" element={<PageWrapper Component={AditivosStorage} />} />
          <Route path="/aditivos/*" element={<AditivosHomeRedirect />} />
          
          {/* Servicios Routes */}
          <Route path="/servicios" element={<Navigate to="/servicios/maintenance" replace />} />
          <Route path="/servicios/maintenance" element={<PageWrapper Component={ServiciosMaintenance} />} />
          <Route path="/servicios/repairs" element={<PageWrapper Component={ServiciosRepairs} />} />
          <Route path="/servicios/installation" element={<PageWrapper Component={ServiciosInstallation} />} />
          <Route path="/servicios/support" element={<PageWrapper Component={ServiciosSupport} />} />
          <Route path="/servicios/billing/quotes" element={<PageWrapper Component={ServiciosSupport} />} />
          <Route path="/servicios/billing/invoices" element={<PageWrapper Component={ServiciosSupport} />} />
          <Route path="/servicios/billing/payments" element={<PageWrapper Component={ServiciosSupport} />} />
          
          {/* Perfil de usuario */}
          <Route path="/perfil" element={<PageWrapper />} />
          <Route path="/agenda" element={<PageWrapper />} />

          {/* Home / unknown: first path the user can actually use */}
          <Route path="/" element={<HomeRedirect />} />
          <Route path="*" element={<HomeRedirect />} />
        </Routes>
      </ContentArea>
    </MainLayout>
    <MaintenanceNoticeGate />
    {presenceOpen && canViewPresence ? (
      <div
        role="dialog"
        style={{
          position: 'fixed',
          left: 16,
          bottom: 48,
          zIndex: 1100,
          minWidth: 260,
          maxWidth: 360,
          maxHeight: 320,
          overflow: 'auto',
          background: isDarkMode ? '#1f1f1f' : '#fff',
          color: isDarkMode ? '#eee' : '#111',
          border: `1px solid ${isDarkMode ? '#444' : '#ddd'}`,
          borderRadius: 8,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          padding: 12,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
          <strong>Usuarios activos</strong>
          <button type="button" onClick={() => setPresenceOpen(false)} style={{ border: 'none', background: 'transparent', cursor: 'pointer' }}>
            ✕
          </button>
        </div>
        {activeUsers.length === 0 ? (
          <p style={{ margin: 0, fontSize: 13, opacity: 0.8 }}>No hay otros usuarios activos ahora.</p>
        ) : (
          <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
            {activeUsers.map((u) => (
              <li key={u.id} style={{ marginBottom: 4 }}>
                {u.full_name || u.email}
                {Array.isArray(u.role_codes) && u.role_codes.length
                  ? ` (${u.role_codes.join(', ')})`
                  : ''}
              </li>
            ))}
          </ul>
        )}
      </div>
    ) : null}
    </>
  );
};

// Main App Router Component
const AppRouter = () => {
  return (
    <Router>
      <AppLayout />
    </Router>
  );
};

export default AppRouter;
